import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import { Outlet } from 'react-router-dom';

import useContextHook from './useContextHook';
import usePrevious from '../hooks/usePrevious';
import {
  fetchAuthSession,
  fetchUserAttributes,
  signOut
} from '@aws-amplify/auth';

const Context = createContext(null);
Context.displayName = 'User';

export const Provider = () => {
  const [userData, setUserData] = useState(null);
  const [isSessionValid, setIsSessionValid] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [logOutAction, setLogOutAction] = useState('');
  const prevIsSessionValid = usePrevious(isSessionValid);
  const avatarSrc = '';

  const checkSessionStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const session = await fetchAuthSession();
      if (session && session.tokens === undefined) {
        setIsSessionValid(false);
      } else {
        setIsSessionValid(!!session);
      }
      return session;
    } catch (error) {
      console.error('Error fetching session:', error);
      setIsSessionValid(false);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUserData = useCallback(async () => {
    if (!isSessionValid) return null;

    try {
      const result = await fetchUserAttributes();
      const userSession = await fetchAuthSession();
      const userRole =
        userSession?.tokens?.accessToken?.payload?.['cognito:groups']?.[0] ||
        'Viewer';
      const userData = { ...result, userRole };

      if (result) {
        setUserData((prevUserData) =>
          JSON.stringify(result) === JSON.stringify(prevUserData)
            ? prevUserData
            : userData
        );
        return userData;
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
    return null;
  }, [isSessionValid]);

  const logOut = useCallback(async (action) => {
    setLogOutAction(action);
    try {
      await signOut();
      console.log('User logged out');
      setIsSessionValid(false);
      setUserData(null);
    } catch (error) {
      console.error('Error signing out:', error);
      setIsSessionValid(false);
      setUserData(null);
    }
  }, []);

  const refreshUserSession = useCallback(() => {
    setIsLoading(true);
    const session = checkSessionStatus();
    if (session && isSessionValid) {
      fetchUserData();
    }
    setIsLoading(false);
  }, [checkSessionStatus, fetchUserData, isSessionValid]);

  // Initial session check on page load
  useEffect(() => {
    checkSessionStatus();
  }, [checkSessionStatus]);

  // Fetch user data when session becomes valid
  useEffect(() => {
    if (isSessionValid && !userData) {
      fetchUserData();
    }
  }, [isSessionValid, fetchUserData, userData]);

  const value = useMemo(
    () => ({
      checkSessionStatus,
      fetchUserData,
      isSessionValid,
      logOut,
      logOutAction,
      prevIsSessionValid,
      refreshUserSession,
      userData: userData && { ...userData, avatarSrc },
      isLoading
    }),
    [
      avatarSrc,
      checkSessionStatus,
      fetchUserData,
      isSessionValid,
      logOut,
      logOutAction,
      prevIsSessionValid,
      refreshUserSession,
      userData,
      isLoading
    ]
  );

  return (
    <Context.Provider value={value}>
      <Outlet />
    </Context.Provider>
  );
};

export const useUser = () => useContextHook(Context);
